<template>
  <svg
    :width="diameter"
    :height="diameter"
    class="block"
    :style="{
      width: diameter + 'px',
      height: diameter + 'px',
    }"
  >
    <circle
      :class="trackStrokeColorClass"
      :cx="radius"
      :cy="radius"
      :r="adjustedRadius"
      :stroke-width="trackThickness"
      :stroke-dasharray="circumference"
      :stroke-dashoffset="animatedStrokeDashoffsetTrack"
      stroke-linecap="round"
      :transform="rotateTrack"
      :transform-origin="`${radius} ${radius}`"
      fill="transparent"
    />
    <circle
      :class="indicatorStrokeColorClass"
      :cx="radius"
      :cy="radius"
      :r="adjustedRadius"
      :stroke-width="thickness"
      fill="transparent"
      :stroke-dasharray="circumference"
      :stroke-dashoffset="animatedStrokeDashoffset"
      stroke-linecap="round"
      :transform="rotateValue"
      :transform-origin="`${radius} ${radius}`"
    />
  </svg>
</template>

<script setup lang="ts">
  import { useTransition } from '@vueuse/core'
  import { computed, ref, watch } from 'vue'

  interface Props {
    percentage: number
    radius?: number
    thickness?: number
    disabled?: boolean
    color?: 'primary' | 'secondary' | 'tertiary' | 'neutral'
  }

  const {
    disabled,
    percentage,
    radius = 12,
    thickness = 8,
    color = 'secondary',
  } = defineProps<Props>()

  const trackStrokeColorClass = computed(() => {
    if (color == 'neutral' || disabled) {
      return 'stroke-outline-variant'
    }
    if (color == 'primary') {
      return 'stroke-primary-container'
    }
    if (color == 'tertiary') {
      return 'stroke-tertiary-container'
    }
    if (color == 'secondary') {
      return 'stroke-secondary-container'
    }
  })

  const indicatorStrokeColorClass = computed(() => {
    if (color == 'neutral' || disabled) {
      return 'stroke-outline'
    }
    if (color == 'primary') {
      return 'stroke-primary'
    }
    if (color == 'tertiary') {
      return 'stroke-tertiary'
    }
    if (color == 'secondary') {
      return 'stroke-secondary'
    }
  })
  const valueSet = ref(false)
  const adjustedRadius = computed(() => radius - thickness / 2)

  const diameter = computed(() => radius * 2)
  const circumference = computed(() => 2 * Math.PI * adjustedRadius.value)

  const gapLength = computed(() => (percentage == 0 ? 0 : 4))

  const strokeDashoffset = ref(circumference.value)

  const strokeDashoffsetTrack = computed(() => {
    if (valueSet.value) {
      return (percentage / 100) * -circumference.value - trackThickness.value - 2 * gapLength.value
    }
    return -gapLength.value * 2 - thickness * 2
  })

  const rotateValue = computed(() => {
    return `rotate(${-90 + (thickness / 2 / circumference.value) * 360} )`
  })

  const rotateTrack = computed(() => {
    return `rotate(${-90 - (trackThickness.value / 2 / circumference.value) * 360 - (gapLength.value / circumference.value) * 360} )`
  })

  const updateProgress = () => {
    if (percentage == 100) {
      strokeDashoffset.value = (1 - percentage / 100) * circumference.value
    } else if (percentage == 0) {
      strokeDashoffset.value = circumference.value
    } else {
      strokeDashoffset.value = (1 - percentage / 100) * circumference.value + thickness
    }
  }

  const animatedValue = useTransition(strokeDashoffsetTrack, {
    duration: 1000,
    transition: [0.31, 0.94, 0.34, 1],
  })

  const animatedStrokeDashoffsetTrack = computed(() => {
    if (remainingArcLength.value <= thickness + 2 * gapLength.value) {
      return -circumference.value * 1.01
    }
    return animatedValue.value
  })

  const trackThickness = computed(() => {
    if (remainingArcLength.value <= thickness + 2 * gapLength.value) {
      return remainingArcLength.value - 2 * gapLength.value
    }
    return thickness
  })

  const remainingArcLength = computed(() => {
    return Math.abs(animatedStrokeDashoffset.value) - thickness
  })

  const valueStrokeLinecap = ref<'round' | 'square'>('round')

  const animatedStrokeDashoffset = useTransition(strokeDashoffset, {
    duration: 1000,
    transition: [0.31, 0.94, 0.34, 1],
    onFinished: () => {
      if (percentage == 100) {
        valueStrokeLinecap.value = 'square'
      }
    },
  })

  watch(
    () => percentage,
    () => {
      setTimeout(() => {
        updateProgress()
        valueSet.value = true
      }, 10)
    },
    { immediate: true },
  )
</script>
